import { columnProps } from "@/components/ColumnMenu";
import Export from "@/components/Export";
import SearchBox from "@/components/SearchBox";
import { useSearchFilter } from "@/hooks/useSearchFilters";
import {
  useAllUsers,
  useCreateUser,
  useUpdateUser,
  useUsers,
} from "@/hooks/useUsers";
import { IUser } from "@/interfaces/user.interface";
import { userSvc } from "@/services/user.service";
import { userGroupSvc } from "@/services/userGroup.service";
import { RootState } from "@/store/store";
import {
  companyFilters,
  companyUserFilter,
  getPaginationData,
} from "@/utils/kendo";
import ToastService from "@/utils/toast";
import { Button } from "@progress/kendo-react-buttons";
import { ENGINEER, SUBCONTRACTOR, SUPER_ADMIN } from "@/constants/roles";
//@ts-ignore
import { PagerTargetEvent } from "@progress/kendo-react-data-tools/pager/Pager";
import {
  ComboBoxChangeEvent,
  MultiSelectChangeEvent,
} from "@progress/kendo-react-dropdowns";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import {
  GridColumn as Column,
  Grid,
  GridDataStateChangeEvent,
  GridPageChangeEvent,
  GridToolbar,
} from "@progress/kendo-react-grid";
import {
  CheckboxChangeEvent,
  Switch,
  TextAreaChangeEvent,
  TextBoxChangeEvent,
} from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { Plus } from "lucide-react";
import { createRef, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import Filters from "../../constants/filters";
import IPageState from "../../interfaces/page.interface";
import { useAppSelector } from "../../store/hooks/hooks";
import WriteDrawer from "./WriteDrawer";
import { columnInterface, generateColumns } from "./columns";
import { isAuthorize } from "@/utils/common";
import { queryClient } from "@/lib/queryClient";
import { queryKeys } from "@/constants/queryKeyFactory";
import { useAllCompanies, useCompanies } from "@/hooks/useCompanies";

let mappingChanged = false;

export const Listing = () => {
  const { selectedProject, projects } = useAppSelector(
    (state: RootState) => state.projects
  );
  const { user } = useAppSelector((state: RootState) => state.user);
  const dispatch = useDispatch();
  const _exporter = createRef<ExcelExport>();
  const Pagination = getPaginationData();
  const createUserMutation = useCreateUser();

  const { data: companies, refetch: fetchCompanies } = useAllCompanies(
    {
      filter: companyUserFilter(selectedProject?.id),
    },
    {
      enabled: !!user?.id,
    }
  );

  const updateUserMutation = useUpdateUser();
  const [addNewUser, setAddNewUser] = useState<IUser | null>(null);
  const [columnsMapping, setColumnsMapping] = useState<any>(null);
  const [page, setPage] = useState<IPageState>(Pagination.initialDataState);
  const [pageSizeValue, setPageSizeValue] = useState<
    number | string | undefined
  >();
  const [showDrawer, setShowDrawer] = useState(false);
  const [users, setUsers] = useState<IUser[]>([]);
  const [stateColumns, setStateColumns] = useState<any>(
    generateColumns(handleEditClick, onColumnsSubmit, null, enableDisableUser, selectedProject?.id)
  );
  const [showArchived, setShowArchived] = useState(false);

  const { searchValue, dataState, handleSearchChange, setDataState } =
    useSearchFilter({
      initialState: Pagination.initialDataState,
      searchFields: ["name", "email"],
    });

  const [isExisting, setIsExisting] = useState(false);

  const filterParams = useMemo(
    () => ({
      pageNumber: page.skip,
      pageSize: page.take,
      sort: dataState.sort ?? null,
      filter: companyFilters(
        "user",
        selectedProject?.id,
        dataState.filter,
        showArchived
      ),
    }),
    [
      page.skip,
      page.take,
      dataState.sort,
      selectedProject?.id,
      dataState.filter,
      showArchived,
    ]
  );

  const { data: usersData, refetch } = useUsers(filterParams, {
    enabled: !!user?.id,
  });

  const { data: allUsersData, refetch: refetchAllUsers } = useAllUsers({
    filter: companyFilters("user", selectedProject?.id),
  });

  const excelExport = () => {
    if (_exporter.current) {
      if (usersData?.data && usersData.data.length > 0) {
        // Get the formatted data

        // Save with the formatted data
        _exporter.current.save(usersData?.data);
      } else {
        refetch().then(() => {
          setTimeout(() => {
            _exporter.current?.save(usersData?.data || []);
          }, 300);
        });
      }
    }
  };

  function onColumnsSubmit(columnsState: Array<columnInterface>) {
    const columnsMapping: any = [];
    for (let col of columnsState) {
      if (col.show) {
        columnsMapping.push(col.field);
      }
    }
    setColumnsMapping(columnsMapping);
    mappingChanged = true;
    const cols = generateColumns(
      handleEditClick,
      onColumnsSubmit,
      columnsState,
      enableDisableUser,
      selectedProject?.id
    );
    setStateColumns(cols);
  }

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take = targetEvent.value === "All" ? users.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const handleChange = (
    e:
      | TextBoxChangeEvent
      | TextAreaChangeEvent
      | CheckboxChangeEvent
      | ComboBoxChangeEvent
      | MultiSelectChangeEvent
  ) => {
    if (e.target.name === "id") {
      setAddNewUser(e.value);
      return;
    }

    if (e.target.name === "companyId") {
      setAddNewUser((prev: any) => {
        return {
          ...prev,
          [e.target.name as any]: e.value?.id,
          companyName: e.value?.name,
        };
      });
      return;
    }
    if (e.target.name === "projects") {
      setAddNewUser((prev: any) => {
        return {
          ...prev,
          projects: e.value?.map((item: any) => ({ id: item.id, isActive: true })),
        };
      });
      return;
    }
    if (e.target.name === "roles") {
      setAddNewUser((prev: any) => {
        return { ...prev, roles: e.value };
      });
      return;
    }

    if (e.target.name === "userGroups") {
      setAddNewUser((prev: any) => {
        return { ...prev, userGroups: e.value?.map((item: any) => item.id) };
      });
      return;
    }

    if (e.target.name === "idProvider") {
      setAddNewUser((prev: any) => {
        return { ...prev, idProvider: e.value?.id };
      });
      return;
    }

    setAddNewUser((prev: any) => {
      return { ...prev, [e.target.name as any]: e.value };
    });
  };

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    setDataState(event.dataState);
  };

  const createUser = () => {
    if (!addNewUser) return;
    if (!addNewUser?.roles?.length) {
      ToastService.error("Please select at least one role");
      return;
    }

    const isSuperAdmin =
      Array.isArray(addNewUser?.roles) &&
      addNewUser?.roles.some((role: string) => {
        return role === SUPER_ADMIN;
      });

    if (addNewUser?.roles?.length > 0) {
      if (!isSuperAdmin && !selectedProject) {
        ToastService.error(
          "Please select a project as this user is a not a super admin"
        );
        return;
      }
    }

    if (isAuthorize([SUPER_ADMIN]) && !addNewUser?.projects?.length) {
      ToastService.error("Please select a project first");
      return;
    }

    if (
      !isAuthorize([SUPER_ADMIN]) &&
      (!selectedProject || !selectedProject?.id)
    ) {
      ToastService.error("Please select a project first");
      return;
    }

    const isSubcon =
      Array.isArray(addNewUser?.roles) &&
      addNewUser?.roles.some((role: string) => {
        return role === SUBCONTRACTOR;
      });

    if (isSubcon && !addNewUser?.companyId) {
      ToastService.error("Please select a company for this subcontractor");
      return;
    }

    createUserMutation.mutate(
      {
        ...addNewUser,
        isActive: true,
      },
      {
        onSuccess: (res) => {
          if (res.data?.id) {
            userSvc.getById<IUser>(res.data.id).then((user) => {
              if (user?.data) {
                const newUsers = {
                  ...user.data,
                  projects:
                    isAuthorize([SUPER_ADMIN]) && isSuperAdmin
                      ? addNewUser?.projects
                      : [{ id: selectedProject!.id!, isActive: true }],
                  companyId: addNewUser?.companyId,
                  companyName: addNewUser?.companyName,
                };
                updateUserMutation.mutate(
                  { id: user.data.id, data: newUsers },
                  {
                    onSuccess: async () => {
                      // Sync user groups if provided
                      if (addNewUser?.userGroups?.length && user?.data?.id) {
                        try {
                          const isEngineer = addNewUser?.roles?.includes(ENGINEER);
                          if (isEngineer) {
                            await userGroupSvc.syncEngineerInGroups(
                              user.data.id,
                              addNewUser.userGroups
                            );
                          } else {
                            await userGroupSvc.syncUserInGroups(
                              user.data.id,
                              addNewUser.userGroups
                            );
                          }
                        } catch (error) {
                          console.error("Failed to sync user groups:", error);
                        }
                      }
                      ToastService.success("User added successfully");
                      queryClient.invalidateQueries({
                        queryKey: queryKeys.company.root,
                      });
                      queryClient.invalidateQueries({
                        queryKey: queryKeys.userGroup.root,
                      });
                      setShowDrawer(false);
                    },
                  }
                );
              }
              return;
            });
          } else {
            setShowDrawer(false);
          }
        },
      }
    );
  };

  const editUser = async () => {
    if (!addNewUser?.id) return;

    const isSuperAdmin =
      Array.isArray(addNewUser?.roles) &&
      addNewUser?.roles.some((role: string) => {
        return role === SUPER_ADMIN;
      });

    if (!isSuperAdmin && !selectedProject) {
      ToastService.error(
        "Please select a project as this user is a not a super admin"
      );
      return;
    }

    if (
      isAuthorize([SUPER_ADMIN]) &&
      !isExisting &&
      !addNewUser?.projects?.length
    ) {
      ToastService.error("Please select a project first");
      return;
    }

    const isSubcon =
      Array.isArray(addNewUser?.roles) &&
      addNewUser?.roles.some((role: string) => {
        return role === SUBCONTRACTOR;
      });

    if (isSubcon && !addNewUser?.companyId) {
      ToastService.error("Please select a company for this subcontractor");
      return;
    }

    const userData = {
      ...addNewUser,
    };

    if (isAuthorize([SUPER_ADMIN]) && !isExisting) {
      userData.projects = addNewUser?.projects;
    } else {
      const alreadyMember = userData.projects?.some(
        (p) => p.id === selectedProject!.id
      );
      if (!alreadyMember) {
        userData.projects = [
          ...(userData.projects || []),
          { id: selectedProject!.id!, isActive: true },
        ];
      }
    }

    updateUserMutation.mutate(
      { id: userData.id!, data: userData },
      {
        onSuccess: async () => {
          // Sync user groups if provided
          if (addNewUser?.userGroups !== undefined) {
            try {
              const isEngineer = addNewUser?.roles?.includes(ENGINEER);
              if (isEngineer) {
                await userGroupSvc.syncEngineerInGroups(
                  userData.id!,
                  addNewUser.userGroups || []
                );
              } else {
                await userGroupSvc.syncUserInGroups(
                  userData.id!,
                  addNewUser.userGroups || []
                );
              }
            } catch (error) {
              console.error("Failed to sync user groups:", error);
            }
          }
          ToastService.success("User updated successfully");
          queryClient.invalidateQueries({
            queryKey: queryKeys.company.root,
          });
          queryClient.invalidateQueries({
            queryKey: queryKeys.userGroup.root,
          });
          setShowDrawer(false);
        },
      }
    );
  };

  function enableDisableUser(dataItem: IUser) {
    if (!dataItem?.id || !selectedProject?.id) return;
    const isCurrentlyActive = dataItem.projects?.some(
      (p) => p.id === selectedProject.id && p.isActive
    ) ?? false;
    userSvc.toggleProjectMembership(dataItem.id, selectedProject.id, !isCurrentlyActive)
      .then(() => {
        ToastService.success("User updated successfully");
        queryClient.invalidateQueries({ queryKey: queryKeys.users.root });
      });
  }

  function handleEditClick(data: IUser) {
    setAddNewUser(data);
    setShowDrawer(true);
    setIsExisting(false);
  }

  useEffect(() => {
    if (!showDrawer) {
      setAddNewUser(null);
    } else {
      fetchCompanies();
    }
  }, [showDrawer]);

  useEffect(() => {
    if (usersData?.data) {
      setUsers(usersData.data);
    }
  }, [usersData]);

  useEffect(() => {
    if (!users?.length || !columnsMapping) return;
    const cols = generateColumns(
      handleEditClick,
      onColumnsSubmit,
      null,
      enableDisableUser,
      selectedProject?.id
    );
    setStateColumns(cols);
  }, [users, columnsMapping]);

  useEffect(() => {
    setStateColumns(
      generateColumns(
        handleEditClick,
        onColumnsSubmit,
        null,
        enableDisableUser,
        selectedProject?.id
      )
    );
  }, [selectedProject?.id]);

  useEffect(() => {
    if (!allUsersData?.data?.length) return;
    // _exporter.current?.save();
  }, [allUsersData]);

  return (
    <>
      <div className="setting-page">
        <Grid
          style={{ maxHeight: "" }}
          data={users}
          {...dataState}
          onDataStateChange={dataStateChange}
          skip={page.skip}
          take={page.take}
          total={usersData?.total || 0}
          pageable={{
            ...Pagination.pageSizeValue,
            pageSizeValue: pageSizeValue,
          }}
          onPageChange={pageChange}
          className={`customHeight employee`}
          resizable={Filters.resizeable}
          selectable={{
            enabled: true,
            drag: true,
            mode: "multiple",
          }}
        >
          <GridToolbar>
            <div className="flex gap-3 w-full items-center">
              <div className="w-[250px]">
                <SearchBox
                  value={searchValue}
                  onChange={handleSearchChange}
                  placeholder="Search"
                />
              </div>
              <Button
                themeColor={"primary"}
                className="border border-[#E2E8F0]"
                onClick={() => setShowDrawer(true)}
              >
                <span className="inline-flex items-center gap-2 font-normal">
                  <Plus className="w-4 h-4" /> Add New
                </span>
              </Button>
              <Button
                themeColor={"primary"}
                className="border border-[#E2E8F0]"
                onClick={() => {
                  setIsExisting(true);
                  setShowDrawer(true);
                }}
              >
                <span className="inline-flex items-center gap-2 font-normal">
                  <Plus className="w-4 h-4" /> Add Existing
                </span>
              </Button>
              <div className="flex items-center gap-2 md:order-3 sm:order-2 order-4 sm:mt-0 mt-2 sm:ml-0 ml-auto">
                <Label className="font-medium text-sm font-cabin">
                  Show Inactive
                </Label>
                <Switch
                  checked={showArchived}
                  onChange={(e) => {
                    setShowArchived(e.value);
                  }}
                />
              </div>
              <div
                className="cursor-pointer sm:ml-0 ml-auto sm:flex-initial flex-1"
                onClick={excelExport}
              >
                <Export />
                <ExcelExport
                  data={allUsersData?.data || []}
                  collapsible={true}
                  fileName="Users.xlsx"
                  ref={_exporter}
                >
                  {stateColumns &&
                    stateColumns.length > 0 &&
                    stateColumns.map((column: any, idx: number) => {
                      if (column.show) {
                        return (
                          <ExcelExportColumn
                            key={idx}
                            field={column.field}
                            title={column.title}
                          />
                        );
                      }
                    })}
                </ExcelExport>
              </div>
            </div>
          </GridToolbar>
          {stateColumns &&
            stateColumns.length > 0 &&
            stateColumns.map((column: any, idx: number) => {
              if (column.show) {
                if (column.cells) {
                  return (
                    <Column
                      {...columnProps(column.field, dataState)}
                      key={idx}
                      field={column.field}
                      title={column.title}
                      columnMenu={column.columnMenu}
                      minResizableWidth={200}
                      cells={column.cells}
                      filter={column.filter ? column.filter : "text"}
                    />
                  );
                }
                return (
                  <Column
                    {...columnProps(column.field, dataState)}
                    key={idx}
                    field={column.field}
                    title={column.title}
                    columnMenu={column.columnMenu}
                    minResizableWidth={200}
                    filter={column.filter ? column.filter : "text"}
                  />
                );
              }
            })}
        </Grid>
      </div>
      <WriteDrawer
        companies={companies?.data}
        showCreateDrawer={showDrawer}
        setShowCreateDrawer={setShowDrawer}
        addNewUser={addNewUser}
        handleChange={handleChange}
        onSave={addNewUser?.id ? editUser : createUser}
        isExisting={isExisting}
        setIsExisting={setIsExisting}
        setAddNewUser={setAddNewUser}
      />
    </>
  );
};
